// 사주 분석 진입점. 서버 SajuService.java(POST /api/saju)를 그대로 옮긴 것이다. 응답 모양도 같다.
import * as C from './calc.js';
import { toLunar, toSolar } from './lunar.js';
import * as Stat from './stats.js';

const round = (v, scale) => Math.round(v * scale) / scale;
const statMap = (stats) => Object.fromEntries(C.GROUPS.map((g, i) => [g, stats[i]]));
const combos = (profile) => profile.combinations.map(({ kind, label, status, detail }) => ({ kind, label, status, detail }));
const intIn = (v, lo, hi) => Number.isInteger(v) && v >= lo && v <= hi;
const optIn = (v, lo, hi) => v == null || intIn(v, lo, hi);
const INVALID = '입력값을 확인해 주세요';

function validate(r) {
  const ok = intIn(r.year, 1900, 2100) && intIn(r.month, 1, 12) && intIn(r.day, 1, 31)
    && optIn(r.hour, 0, 23) && optIn(r.minute, 0, 59)
    && (r.gender == null || r.gender === 'M' || r.gender === 'F')
    && (r.calendar == null || r.calendar === 'SOLAR' || r.calendar === 'LUNAR');
  if (!ok) throw new Error(INVALID);
}

function meanMap(profiles, getter) {
  const values = {};
  for (const key of Object.keys(getter(profiles[0]))) {
    let sum = 0;
    for (const p of profiles) sum += getter(p)[key];
    values[key] = round(sum / profiles.length, 10000);
  }
  return values;
}

function pillarView(p, dayStem, isDay) {
  const ss = isDay ? null : C.sipseong(dayStem, p.stem);
  return { stem: p.stem, branch: p.branch, text: p.text, sipseong: ss, group: ss == null ? null : C.groupOf(ss) };
}

export function analyzeSajuLocal(r) {
  validate(r);
  let y = r.year, m = r.month, d = r.day;
  if (r.calendar === 'LUNAR') {
    const sd = toSolar(y, m, r.leap === true, d);
    y = sd.year; m = sd.month; d = sd.day;
  }
  const ld = toLunar(y, m, d);
  const lunarText = `${ld.year}.${ld.leap ? '윤' : ''}${ld.month}.${ld.day}`;
  const zi = r.ziSplit == null || r.ziSplit;
  const off = r.offsetMin == null ? 0 : r.offsetMin;
  const hour = r.hour == null ? null : r.hour;
  const minute = r.minute == null ? null : r.minute;
  const s = C.compute(y, m, d, hour, minute, zi, off);
  const ds = s.day.stem;

  const initial = Stat.analyze(s, 10);
  const profiles = [];
  let uncertainty = null, stats;
  if (hour == null) {
    // 입력 화면과 같은 대표 시각. 실제 시각·출생 확률을 추정하는 절차가 아니다.
    const hourCases = [];
    for (let branch = 0; branch < 12; branch++) {
      const h = branch * 2;
      const candidate = C.compute(y, m, d, h, 30, zi, off);
      const profile = Stat.analyze(candidate, 10);
      profiles.push(profile);
      hourCases.push({
        label: `${C.BRANCHES[candidate.hour.branch]}시 · ${String(h).padStart(2, '0')}:30`,
        pillars: `${candidate.year.text} ${candidate.month.text} ${candidate.day.text} ${candidate.hour.text}`,
        stats: statMap(profile.stats), combinations: combos(profile),
      });
    }
    const ranges = {};
    stats = {};
    for (let group = 0; group < 5; group++) {
      let min = Infinity, max = -Infinity, sum = 0;
      for (const p of profiles) { const v = p.stats[group]; min = Math.min(min, v); max = Math.max(max, v); sum += v; }
      const mean = round(sum / profiles.length, 10);
      stats[C.GROUPS[group]] = mean;
      ranges[C.GROUPS[group]] = { min, max, mean };
    }
    uncertainty = {
      method: '00:30부터 22:30까지 2시간 간격의 12개 대표 시각을 같은 비중으로 계산했습니다. '
        + '게임은 평균으로 시작하며 시주는 모름으로 유지합니다. 이 범위는 실제 분 단위 경계와 23시 자시의 모든 경우를 포함하지 않습니다. '
        + '상단 세 기둥·성향·대운은 기존 정오 기준 표시이며, 이 표의 평균·범위는 초기 능력치에만 적용됩니다.',
      ranges, cases: hourCases,
    };
  } else {
    profiles.push(initial);
    stats = statMap(initial.stats);
  }

  const el = C.elements(s);
  const elements = Object.fromEntries(C.EL_NAME.map((n, i) => [n, el[i]]));
  const dw = C.daewoon(y, m, d, hour, minute, off, s, r.gender, 9);
  const items = dw.list.map((w) => ({ age: w.age, stem: w.stem, branch: w.branch, text: C.STEMS[w.stem] + C.BRANCHES[w.branch] }));

  const positions = ['년지', '월지', '일지', '시지'];
  let limits = [...initial.limits];
  if (uncertainty) {
    limits = limits.filter((note) => !note.startsWith('시주 모름'));
    limits.push('시주 모름: 초기 능력치와 사건 노출 계수는 12개 대표 시각의 동일 비중 평균이며, 실제 출생시각이나 확률을 뜻하지 않습니다.');
    limits.push('시주 모름: 세 기둥·기질·통근 목록·대운은 기존 정오 기준의 표시입니다. 절입·자시 경계의 전체 불확실성은 별도 미판정입니다.');
  }
  const mean = (f) => profiles.reduce((a, p) => a + f(p), 0) / profiles.length;
  const basis = {
    version: Stat.VERSION,
    tenGodForces: meanMap(profiles, (p) => p.tenGodForces),
    usageFactors: meanMap(profiles, (p) => p.usageFactors),
    daySupportIndex: round(mean((p) => p.daySupportIndex), 10000),
    strengthEstimate: uncertainty ? '12시주 평균(게임 추정)' : initial.strengthEstimate,
    dayRoots: initial.dayRoots.map((root) => `${positions[root.position]} ${C.BRANCHES[root.branch]} ${root.label}`),
    climateScope: uncertainty ? '12개 대표 시각별 조후 보정의 평균' : initial.climateScope,
    climateFactor: round(mean((p) => p.climateFactor), 10000),
    limits,
    eventAffinity: meanMap(profiles, (p) => p.eventAffinity),
    combinations: combos(initial),
    hourUncertainty: uncertainty,
  };

  const pad = (n, w) => String(n).padStart(w, '0');
  return {
    solarDate: `${pad(y, 4)}-${pad(m, 2)}-${pad(d, 2)}`, lunarDate: lunarText,
    year: pillarView(s.year, ds, false), month: pillarView(s.month, ds, false),
    day: pillarView(s.day, ds, true), hour: s.hour ? pillarView(s.hour, ds, false) : null,
    stats, elements, slots: 5, daewoon: { forward: dw.forward, startAge: dw.startAge, list: items }, statBasis: basis,
  };
}
